import { Component, Signal, computed, signal, inject, DestroyRef } from '@angular/core';
import { FormArray, FormBuilder, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { HeaderComponent } from '@shared/layout/header/header.component';
import { ComponentsModule, SnackBarService, StepperItem } from '@mastechnicaegroup/carelinelive-ui';
import { NgIf } from '@angular/common';
import { createRegionFormGroup, RegionSingleResponse, StoreRegionRequest } from '@lib/api/models/region';
import { Router } from '@angular/router';
import { SendableRequest } from '@mastechnicaegroup/carelinelive-cdk/requests';
import { RegionService } from '@lib/region/region.service';
import { createZoneFormGroup, StoreZoneRequest, Zone as ZoneData, ZoneSingleResponse } from '@lib/api/models/zone';
import { CommonModule } from '@angular/common';
import { createPriorityFormGroup, Priority, PrioritySingleResponse, StorePriorityRequest } from '@lib/api/models/priorities';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { distinctUntilChanged, map, of, startWith, switchMap, tap } from 'rxjs';
import { FormValidationService } from '@shared/services/form-validation.service';

export enum Step {
    basicDetails = 'basic-details',
    priorities = 'priorities',
    zones = 'zones',
    providers = 'providers'
}

@Component({
    selector: 'app-regions-add-page',
    imports: [HeaderComponent, ComponentsModule, CommonModule, NgIf, ReactiveFormsModule],
    templateUrl: './regions-add-page.component.html',
    standalone: true,
    styleUrl: './regions-add-page.component.scss'
})
export class RegionsAddPageComponent {
    protected readonly Step = Step;

    protected readonly completedSteps = signal<Set<Step>>(new Set());
    protected readonly regionDetailsFormGroup = createRegionFormGroup();
    protected readonly zoneFormGroup = createZoneFormGroup();
    zonesForm: FormGroup;

    protected readonly priorityFormGroup = createPriorityFormGroup();
    prioritiesForm: FormGroup;

    private readonly _regionService = inject(RegionService);
    private readonly _snackBar = inject(SnackBarService);
    private readonly _router = inject(Router);
    private readonly _destroyRef = inject(DestroyRef);
    private readonly _formValidationService = inject(FormValidationService);

    private regionId = '';

    protected readonly steps: Signal<StepperItem[]> = computed((): StepperItem[] => {
        const completedSteps = this.completedSteps();

        return [
            {
                title: 'Basic Details',
                id: Step.basicDetails,
                status: completedSteps.has(Step.basicDetails) ? 'checked' : undefined
            },
            {
                title: 'Priorities',
                id: Step.priorities,
                status: completedSteps.has(Step.priorities) ? 'checked' : undefined,
                isDisabled: !completedSteps.has(Step.basicDetails)
            },
            {
                title: 'Zones',
                id: Step.zones,
                status: completedSteps.has(Step.zones) ? 'checked' : undefined,
                isDisabled: !completedSteps.has(Step.priorities)
            }
        ];
    });

    addRegionRequest = new SendableRequest<RegionSingleResponse, StoreRegionRequest>({
        request: payload => this._regionService.createRegion(payload!),

        onSend: () => {
            this.regionDetailsFormGroup.disable();
        },

        onSuccess: response => {

            this.regionId = response.data.id;

            this._snackBar.open({
                colour: 'success',
                dismissable: false,
                icon: 'check_circle',
                title: 'Region created successfully',
                actions: [
                    { id: 'undo', label: 'OK' }
                ]
            });

            if (!this.prioritiesForm.pristine) {
                const priorities = this.prioritiesForm.value.priorities;

                priorities.forEach((priority: Priority) => {
                    this.addPriorityToRegion(priority);
                });
            }

            if (!this.zonesForm.pristine) {
                const zones = this.zonesForm.value.zones;

                zones.forEach((zone: ZoneData) => {
                    this.addZoneToRegion(zone);
                });
            }

            this._router.navigate(['/regions', this.regionId]);
        }
    });

    addZoneRequest = new SendableRequest<ZoneSingleResponse, StoreZoneRequest>({
        request: payload => this._regionService.createZone(this.regionId, payload!),

        onSuccess: () => {
            return;
        }
    });

    addPriorityRequest = new SendableRequest<PrioritySingleResponse, StorePriorityRequest>({
        request: payload => this._regionService.createPriority(this.regionId, payload!),

        onSuccess: () => {
            return;
        }
    });

    protected readonly nextStep: Signal<StepperItem> = computed(() => {
        const steps = this.steps();
        const currentStepId = this.currentStepId();

        const currentIndex = steps.findIndex(step => step.id === currentStepId);
        return steps[Math.min(steps.length - 1, currentIndex + 1)];
    });

    protected readonly previousStep: Signal<StepperItem> = computed(() => {
        const steps = this.steps();
        const currentStepId = this.currentStepId();

        const currentIndex = steps.findIndex(step => step.id === currentStepId);
        return steps[Math.max(0, currentIndex - 1)];
    });

    protected currentStepId = signal<Step>(Step.basicDetails);
    protected currentStepValid = false;


    constructor(private fb: FormBuilder) {
        this.zonesForm = this.fb.group({
            zones: this.fb.array([createZoneFormGroup()])
        });

        this.prioritiesForm = this.fb.group({
            priorities: this.fb.array([createPriorityFormGroup()])
        });

        toObservable(this.currentStepId).pipe(
            distinctUntilChanged(),
            switchMap((step) => {
                switch (step) {
                    case Step.zones:
                        return this.zonesForm.valueChanges.pipe(
                            startWith(false),
                            map(() => this.zonesForm.valid)
                        );

                    case Step.priorities:
                        return this.prioritiesForm.valueChanges.pipe(
                            startWith(false),
                            map(() => this.prioritiesForm.valid)
                        );

                    case Step.basicDetails:
                        return this.regionDetailsFormGroup.valueChanges.pipe(
                            startWith(false),
                            map(() => this.regionDetailsFormGroup.valid)
                        );

                    default:
                        return of(true);
                }
            }),
            tap(isValid => {
                this.currentStepValid = isValid;
            }),
            takeUntilDestroyed(this._destroyRef)
        ).subscribe();

    }

    protected goToNextStep(): void {
        const updatedCompletedSteps = new Set(this.completedSteps());
        updatedCompletedSteps.add(this.currentStepId());
        this.completedSteps.set(updatedCompletedSteps);

        this.currentStepId.set(this.nextStep().id as Step);
    }

    protected goToPreviousStep(): void {
        const updatedCompletedSteps = new Set(this.completedSteps());
        updatedCompletedSteps.delete(this.currentStepId());
        this.completedSteps.set(updatedCompletedSteps);
        this.currentStepId.set(this.previousStep().id as Step);
    }

    get zonesFormArray(): FormArray {
        return this.zonesForm.get('zones') as FormArray;
    }

    addZone() {
        this.zonesFormArray.push(createZoneFormGroup());
    }

    addZoneToRegion(zone: ZoneData) {
        this.addZoneRequest.send(zone);
    }

    removeZone(index: number) {
        this.zonesFormArray.removeAt(index);

        if (this.zonesFormArray.length === 0) {
            this.addZone();
        }
    }

    get prioritiesFormArray(): FormArray {
        return this.prioritiesForm.get('priorities') as FormArray;
    }

    addPriority() {
        this.prioritiesFormArray.push(createPriorityFormGroup());
    }

    addPriorityToRegion(priority: Priority) {
        this.addPriorityRequest.send(priority);
    }

    removePriority(index: number) {
        this.prioritiesFormArray.removeAt(index);

        if (this.prioritiesFormArray.length === 0) {
            this.addPriority();
        }

    }

    createRegion(): void {
        if (this.addRegionRequest.loading()) {
            return;
        }

        if (!this.regionDetailsFormGroup.valid) {
            this._formValidationService.markFormGroupTouched(this.regionDetailsFormGroup);
            
            this._formValidationService.showFormErrors(this.regionDetailsFormGroup, true);
            
            return;
        }

        if (!this.zonesForm.valid && !this.zonesForm.pristine) {
            this.zonesFormArray.controls.forEach(control => {
                this._formValidationService.markFormGroupTouched(control as FormGroup);
            });
            
            this._snackBar.open({
                colour: 'error',
                dismissable: false,
                icon: 'error_outline',
                title: 'Please review the zones and correct any errors before adding.',
                actions: [
                    { id: 'undo', label: 'OK' }
                ]
            });
            
            return;
        }

        if (!this.prioritiesForm.valid && !this.prioritiesForm.pristine) {
            this.prioritiesFormArray.controls.forEach(control => {
                this._formValidationService.markFormGroupTouched(control as FormGroup);
            });
            
            this._snackBar.open({
                colour: 'error',
                dismissable: false,
                icon: 'error_outline',
                title: 'Please review the priorities and correct any errors before adding.',
                actions: [
                    { id: 'undo', label: 'OK' }
                ]
            });
            
            return;
        }

        const regionData: StoreRegionRequest = {
            reference: this.regionDetailsFormGroup.value.reference ?? '',
            name: this.regionDetailsFormGroup.value.name ?? '',
            status: true
        };

        this.addRegionRequest.send(regionData);
    }

    cancel(): void {
        this._router.navigate(['/regions'])
            .then(success => {
                if (!success) {
                    this._snackBar.open({
                        colour: 'error',
                        icon: 'error',
                        title: 'Failed to navigate to the region index page.'
                    });
                    return;
                }
            });
    }
}
